import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { useMemo } from "react";
import { AssetNotFound, AssetScreen } from "@/features/assets/components/AssetScreen";
import { titleOf } from "@/features/assets/media";
import { assetQuery, cachedAsset } from "@/features/assets/queries";
import { assetPage } from "@/features/assets/urls";
import { sessionQuery } from "@/features/auth/queries";
import { isNotFound } from "@/lib/api";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** One of the asset's versions to show instead of its live one, such as one a world pinned. */
interface AssetSearch {
  version?: string;
}

export const Route = createFileRoute("/assets/$assetId/")({
  validateSearch: (search: Record<string, unknown>): AssetSearch =>
    typeof search.version === "string" && UUID.test(search.version)
      ? { version: search.version }
      : {},
  loaderDeps: ({ search }) => ({ version: search.version }),
  loader: async ({ context: { queryClient }, params: { assetId }, deps: { version } }) => {
    if (!UUID.test(assetId)) return { known: undefined };
    const session = await queryClient.ensureQueryData(sessionQuery);
    const response = await queryClient
      .ensureQueryData(assetQuery(assetId, session?.user.id ?? null, version))
      .catch(error => {
        if (isNotFound(error)) throw notFound();
        throw error;
      });
    if (response.asset.slug)
      throw redirect({ ...assetPage(response.asset), search: { version }, replace: true });
    return { known: response.asset };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.known ? `${titleOf(loaderData.known)} · Pax.co` : "Asset · Pax.co" },
    ],
  }),
  component: AssetPage,
  notFoundComponent: AssetNotFound,
});

function AssetPage() {
  const { assetId } = Route.useParams();
  const { version } = Route.useSearch();
  const queryClient = useQueryClient();
  const known = useMemo(() => cachedAsset(queryClient, assetId), [queryClient, assetId]);
  if (!UUID.test(assetId)) return <AssetNotFound />;
  // Another asset, or another of its versions, is another screen, not this one with new words in it.
  return (
    <AssetScreen
      key={`${assetId}:${version ?? ""}`}
      assetId={assetId}
      versionId={version}
      known={known}
    />
  );
}
